import { motion } from "framer-motion";
import { Truck, Satellite, Gauge, ShieldCheck, Users } from "lucide-react";

const IMAGES = [
  { src: "https://images.pexels.com/photos/16023092/pexels-photo-16023092.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", label: "Long-haul in motion" },
  { src: "https://images.pexels.com/photos/3840446/pexels-photo-3840446.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", label: "Marine terminal operations" },
  { src: "https://images.unsplash.com/photo-1638696898556-2c1f3da83f42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxvaWwlMjB0YW5rZXIlMjB0cnVjayUyMHJlZmluZXJ5JTIwZnVlbCUyMHRyYW5zcG9ydCUyMGhpZ2h3YXl8ZW58MHx8fGJsdWV8MTc4OTc4NDkxNHww&ixlib=rb-4.1.0&q=85", label: "Bulk storage & loading" },
];

const FLEET = [
  { cap: "20,000L", name: "Door-to-Door Peddlers", text: "Agile short trucks built for city streets — delivering diesel directly to homes, estates, offices, factories and facilities, right at your doorstep." },
  { cap: "45,000L", name: "Long-Haul Trucks", text: "Interstate workhorses moving bulk product for oil marketers across Nigeria\u2019s highways — dependable day and night, rain or shine." },
  { cap: "60,000L", name: "Heavy Tankers", text: "Maximum-capacity rigs for depot-to-depot transfers and major lifting operations for the nation\u2019s biggest marketers." },
];

const CHIPS = [
  { icon: Satellite, label: "GPS-Tracked Fleet" },
  { icon: Gauge, label: "Precision Metering" },
  { icon: ShieldCheck, label: "HSE & Safety Compliance" },
  { icon: Users, label: "Trained, Vetted Drivers" },
];

const EASE = [0.16, 1, 0.3, 1];

export default function Fleet() {
  return (
    <section id="fleet" className="py-24 md:py-32 bg-[#0E1733] border-y border-slate-800 overflow-hidden" data-testid="fleet-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="max-w-3xl"
        >
          <p className="text-amber-500 font-display font-semibold tracking-[0.35em] uppercase text-xs mb-4" data-testid="fleet-eyebrow">Our Fleet</p>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl lg:text-5xl text-white leading-tight" data-testid="fleet-heading">
            Built to Move the Nation&rsquo;s <span className="text-gradient-amber">Energy</span>
          </h2>
          <p className="mt-5 text-slate-400 leading-relaxed" data-testid="fleet-description">
            From nimble 20,000-litre door-to-door peddlers to 45,000-litre long trucks and 60,000-litre heavy tankers, our fleet is the backbone of our promise. Every journey is planned, tracked and executed with discipline and dignity.
          </p>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-3 gap-5" data-testid="fleet-gallery">
          {IMAGES.map(({ src, label }, i) => (
            <motion.figure
              key={label}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.12, ease: EASE }}
              className={`group relative overflow-hidden rounded-2xl border border-slate-700/50 ${i === 1 ? "md:mt-10" : ""}`}
              data-testid={`fleet-photo-${i}`}
            >
              <img src={src} alt={label} className="w-full aspect-[4/5] object-cover grayscale-[55%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/90 via-transparent to-transparent" />
              <figcaption className="absolute bottom-4 left-4 text-xs font-display font-semibold tracking-[0.2em] uppercase text-amber-400">{label}</figcaption>
            </motion.figure>
          ))}
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-6" data-testid="fleet-capacity">
          {FLEET.map(({ cap, name, text }, i) => (
            <motion.div
              key={name}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: EASE }}
              className="bg-[#1C2541]/60 border border-slate-700/50 rounded-2xl p-8 hover:border-amber-600/50 hover:-translate-y-1.5 transition-all duration-300"
              data-testid={`fleet-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-amber-600/15 flex items-center justify-center">
                  <Truck size={22} className="text-amber-500" />
                </div>
                <span className="font-mono-stat text-2xl font-bold text-amber-500">{cap}</span>
              </div>
              <h3 className="font-display font-bold text-lg text-white">{name}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{text}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="mt-10 flex flex-wrap gap-3"
          data-testid="fleet-chips"
        >
          {CHIPS.map(({ icon: Icon, label }) => (
            <span key={label} className="flex items-center gap-2 text-xs font-semibold text-slate-300 bg-[#1C2541]/70 border border-slate-700/50 rounded-full px-4 py-2" data-testid={`chip-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <Icon size={14} className="text-amber-500" /> {label}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
